import type { TooltipModel } from 'chart.js';
import { type CSSProperties, forwardRef, type ReactNode, useMemo } from 'react';
import styled from 'styled-components';

import { VFlex } from '@/components/base';
import useColor from '@/hooks/domain/useColor';
import { animation } from '@/styles/style';
import zIndex from '@/styles/zIndex';
import { getQuadrant } from '@/utils/dom';

type ChartTooltipShellProps = {
  tooltip: TooltipModel<any>;
  interactive?: boolean;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
};

export const ChartTooltipShell = forwardRef<
  HTMLDivElement,
  ChartTooltipShellProps
>(function ChartTooltipShell(
  { tooltip, interactive = false, children, className, style },
  ref,
) {
  const color = useColor();

  const pos = useMemo(() => {
    const chartRect = tooltip.chart.canvas.getBoundingClientRect();
    const quadrant = getQuadrant(
      {
        width: chartRect.width,
        height: chartRect.height,
      },
      {
        top: tooltip._eventPosition.y,
        left: tooltip._eventPosition.x,
      },
    );

    const width = 220;
    const height = 150;
    const center = {
      x: tooltip._eventPosition.x - width / 2,
      y: tooltip._eventPosition.y - height / 2,
      x2: tooltip._eventPosition.x + width / 2,
      y2: tooltip._eventPosition.y + height / 2,
    };

    switch (quadrant) {
      case 1:
        return {
          right:
            center.x2 > chartRect.width - 10 ? 10 : chartRect.width - center.x2,
          top: center.y < 10 ? 10 : center.y,
        };
      case 2:
        return {
          left: center.x < 10 ? 10 : center.x,
          top: center.y < 10 ? 10 : center.y,
        };
      case 3:
        return {
          left: center.x < 10 ? 10 : center.x,
          bottom:
            center.y2 > chartRect.height - 10
              ? 10
              : chartRect.height - center.y2,
        };
      case 4:
      default:
        return {
          right:
            center.x2 > chartRect.width - 10 ? 10 : chartRect.width - center.x2,
          bottom:
            center.y2 > chartRect.height - 10
              ? 10
              : chartRect.height - center.y2,
        };
    }
  }, [tooltip]);

  return (
    <TooltipShell
      ref={ref}
      className={className}
      style={{
        ...pos,
        ...style,
        position: 'absolute',
        zIndex: zIndex.chartTooltip,
        border: `1px solid ${color.gray300}`,
        boxShadow: `0px 2px 16px ${color.FD08}`,
        backgroundColor: color.gray100,
        pointerEvents: interactive ? 'auto' : 'none',
        userSelect: interactive ? 'auto' : 'none',
      }}
    >
      {children}
    </TooltipShell>
  );
});

const TooltipShell = styled(VFlex)`
  padding: 12px;
  min-width: 200px;
  white-space: nowrap;
  border-radius: 10px;
  animation: ${animation.fadeIn} 0.2s ease-in-out;
`;
